import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { canEditCommunityContent } from '../../../services/communityService';
import { fetchSetlistById, updateSetlist, deleteSetlist } from '../../../services/setlistService';
import { removeEventAttendee, addEventAttendees, updateAttendanceStatus } from '../../../services/attendanceService';
import { fetchEventById, deleteEvent } from '../../../services/eventService'; // Added deleteEvent import
import { fetchSongById } from '../../../services/songService';
import { checkEventEditPermission, hasPart, hasComment } from './utils/eventHelpers';

// Import components
import EventHeader from './components/EventHeader';
import EventTabs from './components/EventTabs';
import SetlistView from './components/setlist/SetlistView';
import AttendeesView from './components/attendees/AttendeesView';
import ChatView from './components/ChatView';

// Import portals
import EventOptionsPortal from './components/portals/EventOptionsPortal';
import SongOptionsPortal from './components/portals/SongOptionsPortal';

// Import modals
import CommentModal from './components/modals/CommentModal';
import EditRoleModal from './components/modals/EditRoleModal';
import BulkEditRoleModal from './components/modals/BulkEditRoleModal';
import EventDetailsModal from '../desktop/event_modal/EventDetailsModal';
import MobileSetlistModal from './components/modals/MobileSetlistModal';
import MobileAttendeesModal from './components/modals/MobileAttendeesModal';
import HeaderImageModal from './components/modals/HeaderImageModal';

import './styles/MobileEventView.css';

/**
 * Main mobile event view component
 * 
 * @param {object} event - Event data
 * @param {function} onEventDataUpdate - Function to update event data
 * @param {function} onNavigateBack - Function to navigate back
 * @param {function} onDelete - Function to delete event
 * @param {string} activeTab - Active tab (optional, parent-controlled)
 * @param {function} onTabChange - Function to change tab (optional, parent-controlled)
 * @returns {JSX.Element} Mobile event view component
 */
function MobileEventView({
  event,
  onEventDataUpdate,
  onNavigateBack,
  onDelete,
  activeTab: parentActiveTab,
  onTabChange: parentOnTabChange
}) {
  const { user, userCommunities } = useAuth();
  const { t } = useLanguage();

  // Check if this is a party event type
  const isPartyEvent = event && event.event_type === 'party';

  // Use local state as fallback if parent doesn't provide activeTab
  // For party events, default to attendees tab since setlist is hidden
  const [localActiveTab, setLocalActiveTab] = useState(isPartyEvent ? 'attendees' : 'setlist');
  
  // State for setlist and songs
  const [setlist, setSetlist] = useState(null);
  const [songsWithParts, setSongsWithParts] = useState({});
  const [loadingSetlist, setLoadingSetlist] = useState(false);
  
  // State for song options menu
  const [activeSongOptionsId, setActiveSongOptionsId] = useState(null);
  const [songMenuPosition, setSongMenuPosition] = useState({ x: 0, y: 0 });
  
  // State for event options menu
  const [showEventOptions, setShowEventOptions] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  
  // State for edit modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showSetlistModal, setShowSetlistModal] = useState(false);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // State for comment modal
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [commentText, setCommentText] = useState('');

  // Edit role modals state
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [editingSong, setEditingSong] = useState(null);
  const [newRole, setNewRole] = useState('');

  // Bulk edit role modal state
  const [showBulkEditRoleModal, setShowBulkEditRoleModal] = useState(false);
  const [bulkRole, setBulkRole] = useState('');

  // Scroll state
  const [scrollY, setScrollY] = useState(0);
  const contentRef = useRef(null);

  // Edit permissions state
  const [canEditEvent, setCanEditEvent] = useState(false);
  
  // Check if user is the organizer of the event
  const [isOrganizer, setIsOrganizer] = useState(false);

  const [showImageModal, setShowImageModal] = useState(false);

  // Add to track updates
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState(0);

  // Add state for deletion loading
  const [isDeleting, setIsDeleting] = useState(false);

  // Use either parent-controlled tab or local tab state
  // If it's a party event and the current tab is 'setlist', switch to 'attendees'
  const currentTab = isPartyEvent && (parentActiveTab === 'setlist' || localActiveTab === 'setlist')
    ? 'attendees'
    : (parentActiveTab !== undefined ? parentActiveTab : localActiveTab);
  
  // Handle tab changes - update parent state if available, otherwise use local state
  const handleTabChange = (tab) => {
    // If this is a party event and trying to switch to setlist tab, redirect to attendees
    const targetTab = isPartyEvent && tab === 'setlist' ? 'attendees' : tab;
    
    if (parentOnTabChange) {
      parentOnTabChange(targetTab);
    } else {
      setLocalActiveTab(targetTab);
    }
  };

  // Add a handler function to open the image modal
  const handleUploadImage = () => {
    setShowEventOptions(false);
    setShowImageModal(true);
  };

  // Track scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        setScrollY(contentRef.current.scrollTop);
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Check edit permissions and organizer status
  useEffect(() => {
    setCanEditEvent(checkEventEditPermission(
      event, 
      user, 
      userCommunities, 
      canEditCommunityContent
    ));
    
    // Check if current user is the organizer - check both organizer_id and owner_id
    if (event && user) {
      // Check both organizer_id and owner_id as different apps might use different field names
      const isUserOrganizer = 
        (event.organizer_id && String(user.id) === String(event.organizer_id)) ||
        (event.owner_id && String(user.id) === String(event.owner_id));
      
      setIsOrganizer(isUserOrganizer);
    } else {
      setIsOrganizer(false);
    }
  }, [event, user, userCommunities]);

  // Fetch setlist and song details when event changes - skip for party events
  useEffect(() => {
    const loadSetlist = async () => {
      // Skip setlist loading for party events
      if (isPartyEvent) {
        setSetlist(null);
        setSongsWithParts({});
        return;
      }
      
      if (!event || !event.setlist_id) {
        // Don't clear existing setlist data if one is already loaded
        // This prevents flashing/clearing when editing
        if (!setlist) {
          setSetlist(null);
          setSongsWithParts({});
        }
        return;
      }
      
      // Only set loading if we don't already have setlist data
      if (!setlist || setlist.id !== event.setlist_id) {
        setLoadingSetlist(true);
      }
      
      try {
        const setlistData = await fetchSetlistById(event.setlist_id);
        
        // Only update if we got valid data or if we don't have setlist data yet
        if (setlistData && setlistData.songs && setlistData.songs.length > 0) {
          setSetlist(prevSetlist => {
            // If it's the same setlist ID and we already have data, 
            // keep the current display state until we've fully loaded the new data
            if (prevSetlist && prevSetlist.id === setlistData.id && setlistData.songs.length === 0) {
              return prevSetlist;
            }
            return setlistData;
          });
          
          // After fetching setlist, fetch song details
          if (setlistData.songs.length > 0) {
            fetchSongPartsData(setlistData.songs);
          }
        } else if (!setlist) {
          // Only set empty state if we don't already have data
          setSetlist(setlistData);
        }
      } catch (error) {
        // Don't clear existing data on error to avoid flashing
      } finally {
        setLoadingSetlist(false);
      }
    };
    
    loadSetlist();
    
    // Reset scroll position when setlist_id changes
    if (contentRef.current && (!setlist || setlist.id !== event?.setlist_id)) {
      contentRef.current.scrollTop = 0;
      setScrollY(0);
    }
  }, [event?.setlist_id, lastUpdateTimestamp, isPartyEvent]);
  
  // Function to fetch song part information
  const fetchSongPartsData = async (songs) => {
    // Skip for party events
    if (isPartyEvent) return;
    
    if (!songs || songs.length === 0) return;
    
    try {
      const songPartsData = {};
      
      // Fetch each song to get part file information
      for (const song of songs) {
        if (!song.song_id) continue;
        
        try {
          const songData = await fetchSongById(song.song_id);
          
          if (songData) {
            // Store the song data with proper ID mapping
            songPartsData[song.song_id] = {
              ...song,           // Include setlist song info (song_role, etc)
              ...songData        // Add all the song data from the API
            };
          }
        } catch (songError) {
          // Continue with other songs
        }
      }

      setSongsWithParts(songPartsData);
    } catch (error) {
      // Error fetching song parts
    }
  };

  // Handle click on a song - view the part file if available
  const handleSongClick = (songId) => {
    if (isPartyEvent || !songId) return;
    
    // Get full song data
    const fullSongData = songsWithParts[songId];
    
    // Only proceed if the song has a part file
    if (fullSongData && fullSongData.has_part_file) {
      // You would normally open a modal here to show the part
      alert("Part file viewing is not yet implemented in this mobile view");
    }
  };

  // Handle song options click
  const handleSongOptionsClick = (e, songId) => {
    // Skip for party events
    if (isPartyEvent) return;
    
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering the song click handler
    
    // If we click on the same song's options that's already open, close it
    if (activeSongOptionsId === songId) {
      setActiveSongOptionsId(null);
      return;
    }
    
    // Get the position of the options button for menu placement
    const buttonRect = e.currentTarget.getBoundingClientRect();
    
    // Place the menu to the left of the button
    setSongMenuPosition({
      x: buttonRect.left, 
      y: buttonRect.bottom
    });
    
    // Open the menu for this song
    setActiveSongOptionsId(songId);
    
    // Close event options if open
    setShowEventOptions(false);
  };
  
  // Handle click on event options (3-dot menu)
  const handleEventOptionsClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Get the position of the options button for menu placement
    const buttonRect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({
      x: buttonRect.right,
      y: buttonRect.bottom
    });
    
    // Toggle options menu
    setShowEventOptions(!showEventOptions);
    
    // Close any open song options
    setActiveSongOptionsId(null);
  };
  
  // Handle edit event details
  const handleEditEvent = () => {
    setShowEventOptions(false);
    setShowDetailsModal(true);
  };
  
  // Handle edit setlist - not available for party events
  const handleEditSetlist = () => {
    if (isPartyEvent) return;
    
    setShowEventOptions(false);
    setShowSetlistModal(true);
  };
  
  // Handle edit attendees
  const handleEditAttendees = () => {
    setShowEventOptions(false);
    setShowAttendeesModal(true);
  };
  
  // Handle delete event
  const handleDeleteEvent = () => {
    setShowEventOptions(false);
    setShowDeleteConfirm(true);
  };
  
  // FIXED: Confirm delete event - now actually calls the API
  const confirmDeleteEvent = async () => {
    if (!event?.id || isDeleting) return;
    
    setIsDeleting(true);
    
    try {
      // Actually delete the event from the server
      await deleteEvent(event.id);
      
      // If the event had a setlist, delete that too to prevent orphaned data
      if (event.setlist_id) {
        try {
          await deleteSetlist(event.setlist_id);
        } catch (setlistError) {
          // Continue even if setlist deletion fails
        }
      }
      
      // Close the modal
      setShowDeleteConfirm(false);
      
      // Call the parent's onDelete callback to update the UI
      if (onDelete) {
        onDelete(event.id);
      }

    } catch (error) {
      alert(t('events.messages.errorDeleting') + ": " + (error.message || t('events.messages.unknownError')));
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle adding/editing comment - not available for party events
  const handleCommentOption = (song) => {
    if (isPartyEvent) return;
    
    // Close the options menu
    setActiveSongOptionsId(null);
    
    // Set the song and existing comment (if any)
    setSelectedSong(song);
    setCommentText(song.comment || '');
    setShowCommentModal(true);
  };

  // Save the comment - not available for party events
  const handleSaveComment = () => {
    if (isPartyEvent || !selectedSong || !setlist) return;
    
    try {
      // Create updated setlist with the new comment
      const updatedSongs = setlist.songs.map(song => {
        if (song.song_id === selectedSong.song_id) {
          return { ...song, comment: commentText };
        }
        return song;
      });
      
      const updatedSetlist = {
        ...setlist,
        songs: updatedSongs
      };
      
      // Update parent component for UI consistency
      if (onEventDataUpdate && event) {
        onEventDataUpdate({
          ...event,
          setlist: updatedSetlist
        });
      }
      
      // Update local state
      setSetlist(updatedSetlist);
      
      // Close the modal
      setShowCommentModal(false);

    } catch (error) {
      alert(t('events.messages.errorSavingComment') || "Error saving comment");
    }
  };

  // Handle moving a song in the setlist - not available for party events
  const handleMoveSong = (songId, direction) => {
    if (isPartyEvent || !setlist || !setlist.songs) return;
    
    const updatedSongs = [...setlist.songs];
    const currentIndex = updatedSongs.findIndex(s => s.song_id === songId);
    
    if (currentIndex < 0) return;
    
    if (direction === 'up' && currentIndex > 0) {
      // Swap with previous song
      [updatedSongs[currentIndex], updatedSongs[currentIndex - 1]] = 
      [updatedSongs[currentIndex - 1], updatedSongs[currentIndex]];
    } else if (direction === 'down' && currentIndex < updatedSongs.length - 1) {
      // Swap with next song
      [updatedSongs[currentIndex], updatedSongs[currentIndex + 1]] = 
      [updatedSongs[currentIndex + 1], updatedSongs[currentIndex]];
    } else {
      return; // No movement needed
    }
    
    // Update the setlist with new order
    const updatedSetlist = {
      ...setlist,
      songs: updatedSongs
    };
    
    // Update local state only
    setSetlist(updatedSetlist);
    
    // Update parent component for UI consistency
    if (onEventDataUpdate && event) {
      onEventDataUpdate({
        ...event,
        setlist: updatedSetlist
      });
    }
  };

  // Handle deleting a song from setlist - FIXED VERSION
const handleDeleteSong = (songId, e, skipConfirm = false) => {
  if (isPartyEvent) return;
  
  // Stop propagation if event is provided
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  
  if (!setlist || !setlist.songs) return;
  
  // Only show confirmation if not skipping it (for bulk operations)
  if (!skipConfirm) {
    if (!window.confirm(t('songbook.deleteSongConfirm') || 'Are you sure you want to delete this song?')) {
      return; // User cancelled
    }
  }
  
  const updatedSongs = setlist.songs.filter(s => s.song_id !== songId);
  
  const updatedSetlist = {
    ...setlist,
    songs: updatedSongs
  };
  
  // Update local state only
  setSetlist(updatedSetlist);
  
  // Update parent component for UI consistency
  if (onEventDataUpdate && event) {
    onEventDataUpdate({
      ...event,
      setlist: updatedSetlist
    });
  }
};

  // Handle editing song role - not available for party events
  const handleEditSongRole = (song, e) => {
    if (isPartyEvent) return;
    
    // Stop propagation if event is provided
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setEditingSong(song);
    setNewRole(song.song_role || '');
    setShowEditRoleModal(true);
  };

  // Save edited song role - not available for party events
  const handleSaveRole = () => {
    if (isPartyEvent || !editingSong || !setlist) return;
    
    const updatedSongs = setlist.songs.map(song => {
      if (song.song_id === editingSong.song_id) {
        return { ...song, song_role: newRole };
      }
      return song;
    });
    
    const updatedSetlist = {
      ...setlist,
      songs: updatedSongs
    };
    
    // Update local state only
    setSetlist(updatedSetlist);
    
    // Update parent component for UI consistency
    if (onEventDataUpdate && event) {
      onEventDataUpdate({
        ...event,
        setlist: updatedSetlist
      });
    }
    
    setShowEditRoleModal(false);
  };

  // Save bulk edited roles - not available for party events
  const handleSaveBulkRole = () => {
    if (isPartyEvent || !setlist) return;
    
    const updatedSongs = setlist.songs.map(song => {
      if (selectedSong.includes(song.song_id)) {
        return { ...song, song_role: bulkRole };
      }
      return song;
    });
    
    const updatedSetlist = {
      ...setlist,
      songs: updatedSongs
    };
    
    setSetlist(updatedSetlist);
    
    // Update setlist on the server
    if (onEventDataUpdate && event) {
      onEventDataUpdate({
        ...event,
        setlist: updatedSetlist
      });
    }
    
    setShowBulkEditRoleModal(false);
  };

  // Add this function to the MobileEventView component - not available for party events
  const handleSaveSetlist = async (updatedSetlist) => {
    if (isPartyEvent || !updatedSetlist || !updatedSetlist.id) {
      return;
    }

    try {
      // First ensure all songs have the correct order property
      const songsWithOrder = updatedSetlist.songs.map((song, index) => ({
        ...song,
        order: index // Ensure proper sequential ordering
      }));
      
      // Create the final setlist object with ordered songs
      const orderedSetlist = {
        ...updatedSetlist,
        songs: songsWithOrder
      };
      
      // Keep reference to current setlist for UI preservation
      const currentDisplayedSetlist = setlist;
      
      // Call the setlistService to update the setlist in the backend
      const savedSetlist = await updateSetlist(orderedSetlist.id, orderedSetlist);
      
      // Important: Update the local setlist state with the saved setlist
      // but maintain UI consistency by preserving the structure
      setSetlist({
        ...savedSetlist,
        songs: savedSetlist.songs || currentDisplayedSetlist.songs || []
      });
      
      // Update the timestamp to force a refresh - use a slight delay to ensure
      // state transitions complete correctly
      setTimeout(() => {
        setLastUpdateTimestamp(Date.now());
      }, 100);
      
      // Update the parent component if needed
      if (onEventDataUpdate && event) {
        onEventDataUpdate({
          ...event,
          setlist_id: savedSetlist.id
        });
      }

      return savedSetlist;
    } catch (error) {
      alert(t('events.messages.errorSavingSetlist'));
      throw error;
    }
  };

  // Update attendee status
  const handleUpdateAttendance = async (status, isExistingAttendee = false) => {
    if (!event || !onEventDataUpdate) return;

    try {
      // First update the local state for immediate UI feedback
      const attendees = [...(event.attendees || [])];
      const existingIndex = attendees.findIndex(a => String(a.user_id) === String(user.id));

      if (existingIndex >= 0) {
        attendees[existingIndex] = {
          ...attendees[existingIndex],
          status: status
        };
      } else {
        attendees.push({
          user_id: user.id,
          display_name: user.displayName || user.display_name || user.email?.split('@')[0],
          email: user.email,
          status: status
        });
      }
      
      // Update local state first for immediate feedback
      const updatedEvent = {
        ...event,
        attendees: attendees
      };
      onEventDataUpdate(updatedEvent);

      // Then call the appropriate backend service based on whether user is an existing attendee
      if (isExistingAttendee || existingIndex >= 0) {
        // Use updateAttendanceStatus for existing attendee
        await updateAttendanceStatus(event.id, user.id, status);
      } else {
        // Use addEventAttendees for new attendee
        await addEventAttendees(event.id, [user.id], status);
      }

      // Do NOT fetch from the server after update
      // Trust the optimistic update and let the UI stay consistent

    } catch (error) {
      alert(t('events.messages.errorUpdatingAttendance') || "Error updating attendance");
    }
  };

  // Remove an attendee from the event
  const handleRemoveAttendee = async (userId) => {
    try {
      await removeEventAttendee(event.id, userId);
      
      // Update local state
      const updatedEvent = await fetchEventById(event.id, { includeAttendees: true });
      
      // Update event data in parent component
      onEventDataUpdate({
        ...event,
        attendees: updatedEvent.attendees || []
      });
    } catch (error) {
      alert(t('events.messages.errorRemovingAttendee'));
    }
  };
  
  // Add multiple attendees to the event
  const handleAddAttendees = async (userIds) => {
    try {
      await addEventAttendees(event.id, userIds);
      
      // Update local state
      const updatedEvent = await fetchEventById(event.id, { includeAttendees: true });
      
      // Update event data in parent component
      onEventDataUpdate({
        ...event,
        attendees: updatedEvent.attendees || []
      });
    } catch (error) {
      throw error; // Re-throw to handle in modal
    }
  };
  
  // Handle event details saved
  const handleDetailsSaved = () => {
    setShowDetailsModal(false);
    if (onEventDataUpdate && event) {
      onEventDataUpdate(event);
    }
  };
  
  // Handle setlist saved - UPDATED to handle the returned setlist data - not available for party events
  const handleSetlistSaved = (updatedSetlist) => {
    if (isPartyEvent) {
      setShowSetlistModal(false);
      return;
    }
    
    setShowSetlistModal(false);
    
    // Check if we received an updated setlist from the modal
    if (updatedSetlist) {
      // Store current songs to maintain structure if needed
      const currentSongs = setlist ? setlist.songs : [];
      
      // Update the local setlist state with proper fallbacks
      setSetlist({
        ...updatedSetlist,
        songs: updatedSetlist.songs || currentSongs || []
      });
      
      // Trigger a refresh of song parts data if we have songs
      if (updatedSetlist.songs && updatedSetlist.songs.length > 0) {
        fetchSongPartsData(updatedSetlist.songs);
      }
      
      // Update the timestamp to force a refresh - with a slight delay
      // to ensure state updates are processed correctly
      setTimeout(() => {
        setLastUpdateTimestamp(Date.now());
      }, 100);
      
      // Also update the parent component if needed
      if (onEventDataUpdate && event) {
        onEventDataUpdate({
          ...event,
          setlist_id: updatedSetlist.id || event.setlist_id
        });
      }
    } else {
      // If no setlist data returned, just refresh the current data
      if (event && event.setlist_id) {
        // Force a refresh by updating the timestamp with a slight delay
        setTimeout(() => {
          setLastUpdateTimestamp(Date.now());
        }, 100);
      }
    }
  };

  // If no event data is available
  if (!event) {
    return (
      <div className="updated-event-view">
        <div className="event-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
          <p>{t('events.loading')}</p>
        </div>
      </div>
    );
  }

  // Get the active song data (for the options menu)
  const getActiveSong = () => {
    if (!activeSongOptionsId || !setlist || !setlist.songs) return null;
    
    return setlist.songs.find(song => song.song_id === activeSongOptionsId);
  };

  // Get active song
  const activeSong = getActiveSong();

  return (
    <div className="updated-event-view">
      {/* Scrollable content area with header */}
      <div className="event-scrollable-content" ref={contentRef}>
        {/* Header component */}
        <EventHeader
          event={event}
          scrollY={scrollY}
          onNavigateBack={onNavigateBack}
          onOptionsClick={handleEventOptionsClick}
        />
        
        {/* Tabs component with event data passed for conditional rendering */}
        <EventTabs
          activeTab={currentTab}
          onTabChange={handleTabChange}
          event={event} // Pass the event to check if it's a party type
        />
        
        {/* Tab content */}
        <div className="event-content">
          {currentTab === 'attendees' ? (
            // Attendees content
            <div className="event-section">
              <AttendeesView
                event={event}
                user={user}
                canEdit={canEditEvent}
                onUpdateAttendance={handleUpdateAttendance}
                onRemoveAttendee={handleRemoveAttendee}
                onShowAttendeesModal={() => setShowAttendeesModal(true)}
                isOrganizer={isOrganizer}
              />
            </div>
          ) : currentTab === 'comments' ? (
            // Comments/Chat content
            <div className="event-section">
              <ChatView
                event={event}
                user={user}
                canEdit={true}
              />
            </div>
          ) : !isPartyEvent ? (
            // Setlist content - never shown for party events
            <div className="event-section">
              <SetlistView
                setlist={setlist}
                songsWithParts={songsWithParts}
                canEdit={canEditEvent}
                loadingSetlist={loadingSetlist}
                onSongClick={handleSongClick}
                onSongOptionsClick={handleSongOptionsClick}
                onEditSetlist={handleEditSetlist}
                onMoveSong={handleMoveSong}
                onDeleteSong={handleDeleteSong}
                onEditRole={handleEditSongRole}
                onSaveSetlist={handleSaveSetlist}
              />
            </div>
          ) : (
            // Fallback content for party events when setlist tab is somehow selected
            <div className="event-section">
              <AttendeesView
                event={event}
                user={user}
                canEdit={canEditEvent}
                onUpdateAttendance={handleUpdateAttendance}
                onRemoveAttendee={handleRemoveAttendee}
                onShowAttendeesModal={() => setShowAttendeesModal(true)}
                isOrganizer={isOrganizer}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Event Options Portal Menu - conditionally hide setlist option for party events */}
      <EventOptionsPortal
        show={showEventOptions}
        position={menuPosition}
        onClose={() => setShowEventOptions(false)}
        onEditEvent={handleEditEvent}
        onEditSetlist={isPartyEvent ? null : handleEditSetlist} // Don't include setlist option for party events
        onEditAttendees={handleEditAttendees}
        onDeleteEvent={handleDeleteEvent}
        onUploadImage={handleUploadImage}
        canEdit={canEditEvent}
        isPartyEvent={isPartyEvent} // Pass this to hide setlist option in the portal
      />
      
      {/* Song Options Portal Menu - not shown for party events */}
      {!isPartyEvent && (
        <SongOptionsPortal
          show={!!activeSongOptionsId}
          position={songMenuPosition}
          onClose={() => setActiveSongOptionsId(null)}
          onAddComment={() => activeSong && handleCommentOption(activeSong)}
          onShowNotes={() => activeSongOptionsId && handleSongClick(activeSongOptionsId)}
          hasComment={activeSong && hasComment(activeSong)}
          hasNotes={activeSongOptionsId && hasPart(activeSongOptionsId, songsWithParts)}
        />
      )}
      
      {/* Comment Modal - not shown for party events */}
      {!isPartyEvent && showCommentModal && (
        <CommentModal
          show={showCommentModal}
          onHide={() => setShowCommentModal(false)}
          commentText={commentText}
          setCommentText={setCommentText}
          songTitle={selectedSong?.title}
          onSave={handleSaveComment}
        />
      )}
      
      {/* Edit Role Modal - not shown for party events */}
      {!isPartyEvent && showEditRoleModal && (
        <EditRoleModal
          show={showEditRoleModal}
          onHide={() => setShowEditRoleModal(false)}
          songTitle={editingSong?.title || ''}
          role={newRole}
          setRole={setNewRole}
          onSave={handleSaveRole}
          eventType={event?.event_type}
        />
      )}

      {/* Bulk Edit Role Modal - not shown for party events */}
      {!isPartyEvent && showBulkEditRoleModal && (
        <BulkEditRoleModal
          show={showBulkEditRoleModal}
          onHide={() => setShowBulkEditRoleModal(false)}
          count={0} // This needs to be fixed
          role={bulkRole}
          setRole={setBulkRole}
          onSave={handleSaveBulkRole}
        />
      )}
      
      {/* Event Edit Modals */}
      {showDetailsModal && (
        <EventDetailsModal
          event={event}
          onClose={() => setShowDetailsModal(false)}
          onSave={handleDetailsSaved}
        />
      )}
      
      {/* Updated MobileSetlistModal to handle the returned setlist data - not shown for party events */}
      {!isPartyEvent && showSetlistModal && (
        <MobileSetlistModal
          show={showSetlistModal}
          onHide={() => setShowSetlistModal(false)}
          event={event}
          setlist={setlist}
          onSave={handleSetlistSaved}
        />
      )}
      
      {/* Mobile Attendees Modal - optimized for mobile */}
      <MobileAttendeesModal
        show={showAttendeesModal}
        onHide={() => setShowAttendeesModal(false)}
        event={event}
        existingAttendees={event.attendees || []}
        onAddAttendees={handleAddAttendees}
      />

      {/* Header Image Modal */}
      <HeaderImageModal
        show={showImageModal}
        onHide={() => setShowImageModal(false)}
        event={event}
        onImageUpdate={onEventDataUpdate}
      />
      
      {/* FIXED: Delete Confirmation Modal with proper API call */}
      {showDeleteConfirm && (
        <Modal show={true} onHide={() => setShowDeleteConfirm(false)} centered backdrop="static">
          <Modal.Header closeButton>
            <Modal.Title>{t('events.messages.deleteConfirmTitle')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>{t('events.messages.deleteConfirmMessage', { title: event.title })}</p>
            <p className="text-danger"><strong>{t('common.warning')}:</strong> {t('events.messages.deleteWarning')}</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              {t('events.actions.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={confirmDeleteEvent}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {t('common.deleting') || 'Deleting...'}
                </>
              ) : (
                t('events.actions.deleteEvent')
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default MobileEventView;